import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, ChevronRight, CheckCircle2, XCircle,
  Clock, MoreHorizontal, Download, CreditCard,
  FileText, Shield, UserCheck, BookOpen
} from 'lucide-react';
import { AdminLayout } from './AdminLayout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const StepBadge = ({ step, paymentStatus, contractSigned }) => {
  if (step >= 4) return <span className="px-2.5 py-1 text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-full">Active</span>;
  if (contractSigned) return <span className="px-2.5 py-1 text-xs font-medium bg-rtb-green/10 text-rtb-green border border-rtb-green/20 rounded-full">Contract Signed</span>;
  if (paymentStatus === 'paid') return <span className="px-2.5 py-1 text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded-full">Paid</span>;
  if (step === 1) return <span className="px-2.5 py-1 text-xs font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded-full">Questionnaire Done</span>;
  return <span className="px-2.5 py-1 text-xs font-medium bg-white/[0.06] text-white/40 border border-white/[0.08] rounded-full">New</span>;
};

const ProgressDots = ({ currentDay = 0 }) => {
  const pct = Math.round((currentDay / 20) * 100);
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
        <div className="h-full bg-rtb-purple rounded-full" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-white/40 text-xs">{currentDay}/20</span>
    </div>
  );
};

export default function AdminParticipants() {
  const navigate = useNavigate();
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [stepFilter, setStepFilter] = useState('all');

  const fetchParticipants = async () => {
    try {
      const token = localStorage.getItem('lbu_admin_token');
      const res = await fetch(`${API}/api/admin/participants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setParticipants(data);
    } catch {
      toast.error('Failed to load participants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchParticipants(); }, []);

  const filtered = participants.filter(p => {
    const search = searchTerm.toLowerCase();
    const matchesSearch = !search ||
      p.first_name?.toLowerCase().includes(search) ||
      p.last_name?.toLowerCase().includes(search) ||
      p.email?.toLowerCase().includes(search) ||
      p.phone?.includes(searchTerm);

    if (stepFilter === 'all') return matchesSearch;
    if (stepFilter === 'paid') return matchesSearch && p.payment_status === 'paid';
    if (stepFilter === 'active') return matchesSearch && p.onboarding_step >= 4;
    if (stepFilter === 'onboarding') return matchesSearch && p.onboarding_step < 4;
    if (stepFilter === 'contract') return matchesSearch && p.contract_signed;
    return matchesSearch;
  });

  const stats = {
    total: participants.length,
    paid: participants.filter(p => p.payment_status === 'paid').length,
    active: participants.filter(p => p.onboarding_step >= 4).length,
    onboarding: participants.filter(p => p.onboarding_step < 4).length,
  };

  const handleExportCSV = () => {
    const token = localStorage.getItem('lbu_admin_token');
    fetch(`${API}/api/admin/participants/export`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => { if (!res.ok) throw new Error(); return res.blob(); })
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'participants_export.csv';
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success('CSV exported');
      })
      .catch(() => toast.error('Failed to export'));
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <AdminLayout title="Participants">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Total', value: stats.total, icon: BookOpen, color: 'text-white' },
          { label: 'Paid', value: stats.paid, icon: CreditCard, color: 'text-blue-400' },
          { label: 'Active in Program', value: stats.active, icon: CheckCircle2, color: 'text-emerald-400' },
          { label: 'Onboarding', value: stats.onboarding, icon: Clock, color: 'text-amber-400' },
        ].map(s => (
          <div key={s.label} className="p-4 bg-white/[0.04] border border-white/[0.06] rounded-xl" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
            <s.icon className={`w-5 h-5 ${s.color} mb-2`} />
            <p className="text-2xl font-oswald font-bold text-white">{s.value}</p>
            <p className="text-white/40 text-xs">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
          <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search by name, email, or phone..."
            className="input-dark pl-12" data-testid="participant-search" />
        </div>
        <Button onClick={handleExportCSV} variant="outline" className="border-white/10 text-white/60 hover:text-white" data-testid="export-participants-btn">
          <Download className="w-5 h-5 mr-2" /> Export CSV
        </Button>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {[
          { key: 'all', label: 'All' },
          { key: 'onboarding', label: 'Onboarding' },
          { key: 'paid', label: 'Paid' },
          { key: 'contract', label: 'Contract Signed' },
          { key: 'active', label: 'Active' },
        ].map(f => (
          <button key={f.key} onClick={() => setStepFilter(f.key)}
            className={`px-4 py-2 text-sm font-medium transition-colors rounded-lg ${
              stepFilter === f.key ? 'bg-rtb-purple text-black' : 'bg-charcoal text-white/60 hover:text-white'
            }`} data-testid={`filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Participants Table */}
      <div className="bg-charcoal border border-white/10 overflow-hidden rounded-xl">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-rtb-purple border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-white/40">
            <p>{searchTerm ? 'No matching participants' : 'No participants yet'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table" data-testid="participants-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Contact</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Phase</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => (
                  <tr key={p.id} onClick={() => navigate(`/admin/participants/${p.id}`)}
                    className="cursor-pointer" data-testid={`participant-row-${p.id}`}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-rtb-purple/10 rounded-full flex items-center justify-center">
                          <span className="text-rtb-purple font-bold text-sm">
                            {p.first_name?.[0]}{p.last_name?.[0]}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium">{p.first_name} {p.last_name}</p>
                          {p.recommended_path && (
                            <p className="text-white/30 text-xs capitalize">{p.recommended_path?.replace(/_/g, ' ')}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <p className="text-sm">{p.email}</p>
                      <p className="text-sm text-white/40">{p.phone}</p>
                    </td>
                    <td>
                      <StepBadge step={p.onboarding_step} paymentStatus={p.payment_status} contractSigned={p.contract_signed} />
                    </td>
                    <td>
                      <ProgressDots currentDay={p.current_day || 0} />
                    </td>
                    <td>
                      <span className="text-white/60 text-sm capitalize">
                        {(p.program_phase || 'not started').replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="text-white/60 text-sm">
                      {formatDate(p.created_at)}
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-white/40 hover:text-white">
                            <MoreHorizontal className="w-5 h-5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-charcoal border-white/10">
                          <DropdownMenuItem onClick={() => navigate(`/admin/participants/${p.id}`)} className="cursor-pointer">
                            <FileText className="w-4 h-4 mr-2" /> View Details
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="mt-4 text-white/40 text-sm">
        Showing {filtered.length} of {participants.length} participants
      </div>
    </AdminLayout>
  );
}
